import { useMutation, useQuery } from '@tanstack/react-query';
import {
  accountLinkCompleteRequestSchema,
  accountLinkInspectResultSchema,
  passwordChangeRequestSchema,
  passwordResetRequestSchema,
  type AccountLinkCompleteRequest,
  type PasswordChangeRequest,
  type PasswordResetRequest,
} from '@bekebi/contracts/api';
import { apiRequest } from '../../api/client';
import { AUTH_FLOW_META } from '../../session/auth-actions';

/**
 * One-time account links and passwords (DECISIONS.md A-AC1):
 *   POST /api/auth/account-link/inspect   what an open link is for (changes nothing)
 *   POST /api/auth/account-link/complete  choose the password; uses the link
 *   POST /api/auth/password-reset         "Forgot password?" (always the same answer)
 *   POST /api/auth/password               change one's own password while signed in
 * The link token is sent only in request bodies and kept only in this page's memory; passwords
 * exist only in their forms until submitted.
 */
export function useAccountLink(token: string | null) {
  return useQuery({
    queryKey: ['account-link', token],
    enabled: token !== null,
    queryFn: () =>
      apiRequest('/auth/account-link/inspect', {
        method: 'POST',
        body: { token },
        schema: accountLinkInspectResultSchema,
      }),
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
  });
}

export function useCompleteAccountLink() {
  return useMutation({
    meta: AUTH_FLOW_META,
    mutationFn: (input: AccountLinkCompleteRequest) =>
      apiRequest('/auth/account-link/complete', {
        method: 'POST',
        body: accountLinkCompleteRequestSchema.parse(input),
      }),
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    meta: AUTH_FLOW_META,
    mutationFn: (input: PasswordResetRequest) =>
      apiRequest('/auth/password-reset', {
        method: 'POST',
        body: passwordResetRequestSchema.parse(input),
      }),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: PasswordChangeRequest) =>
      apiRequest('/auth/password', {
        method: 'POST',
        body: passwordChangeRequestSchema.parse(input),
      }),
  });
}
